import { Parameter } from "domain/entities/parameter";
import { EmployeeInputFilter } from "domain/models/employee-input-filter";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import InputDateComponent from "presentation/components/Shared/InputDateComponent";
import InputNumberComponent from "presentation/components/Shared/InputNumberComponent";
import InputTextAreaComponent from "presentation/components/Shared/InputTextAreaComponent";
import InputTextComponent from "presentation/components/Shared/InputTextComponent";
import { usePeopleManagersByFilter } from "presentation/hooks/use-query-employees";
import { useEmployeeStore } from "presentation/stores/use-employee-store";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { usePeopleManagersStore } from "presentation/stores/use-people-manager-store";
import { useEffect, useState } from "react";
import { useJobTitleStore } from "./../../../../stores/use-job-title-store";
import { useEmployeeFieldsStore } from "presentation/stores/use-employee-fields-store";
import { translateText } from "infra/translation/translate";
import { MandatorySignal } from "presentation/components/Shared/MandatorySignalComponent";
import { useWorkLocationsByFilter } from "presentation/hooks/use-query-workLocation";
import { ParameterInputFilter } from "domain/models/parameter-input-filter";

interface ProfessionalInfoFormProps {
  canEdit: boolean;
}

const ProfessionalInfoForm: React.FC<ProfessionalInfoFormProps> = ({
  canEdit,
}) => {
  const [workLocationType, setWorkLocationType] = useState("WorkLocation-PT");
  const { peopleManagers, setPeopleManagers } = usePeopleManagersStore();
  const { employeeFields, setEmployeeFields } = useEmployeeFieldsStore();
  const { employee, setEmployee } = useEmployeeStore();
  const { jobTitle } = useJobTitleStore();
  const {
    contractTypes,
    workLocation,
    workType,
    workCountry,
    employeeStatus,
    isSuportService,
  } = useParameterStore();
  
  // Verifica se tem carta de condução
  const hasDriverLicense = employee?.hasDrivingLicence === true;
  
  const { data: peopleManagersData } = usePeopleManagersByFilter({
    sortBy: "name",
    sortOrder: "asc",
    page: 1,
    limit: 100,
    isPeopleManager: true,
  } as EmployeeInputFilter);
  const { data: workLocationsOptions, refetch: refetechWorkLocations } =
    useWorkLocationsByFilter({
      type: workLocationType,
      sortBy: "value",
      sortOrder: "asc",
      page: 1,
      limit: 100,
    } as ParameterInputFilter);

  const replaceArray = [
    { from: "Ativo", to: true },
    { from: "Inativo", to: false },
  ];

  const replaceArraySupport = [
    { from: "Sim", to: true },
    { from: "Não", to: false },
  ];

  useEffect(() => {
    const managers = peopleManagersData?.data?.map((item) => {
      const peopleManager: Parameter = {
        value: item.name,
        id: item.id,
        type: item.jobTitle.name,
        createdAt: item.createdAt,
        modifiedAt: item.modifiedAt,
      };
      return peopleManager;
    });

    const noManagerOption: Parameter = {
      id: "none",
      value: "NENHUM",
      type: "manager",
    };

    setPeopleManagers([noManagerOption, ...(managers ?? [])]);
  }, [peopleManagersData]);

  useEffect(() => {
    if (employee) {
      if (
        employee?.contractType !== "CTI" &&
        employee?.contractType !== "CTP"
      ) {
        employee.vacationDaysCurrentYear = 0;
        employee.vacationDaysLastYear = 0;
        employee.carOwner = "";
        employee.carRegistration = "";
      }
      if (employee?.contractType !== "CPS Internacional") {
        employee.rfiExpiresAt = null;
      }
      setEmployee(employee);
    }
  }, [employee?.contractType]);

  useEffect(() => {
    if (employee) {
      if (employee.workCountry === "Portugal") {
        setWorkLocationType("WorkLocation-PT");
      } else if (employee.workCountry === "Canadá") {
        setWorkLocationType("WorkLocation-CA");
      } else if (employee.workCountry === "Espanha") {
        setWorkLocationType("WorkLocation-ES");
      }

      const fn = async () => {
        await refetechWorkLocations();
      };
      fn();
    }
  }, [employee?.workCountry]);

  const jobTitles = jobTitle.map((item) => {
    const param: Parameter = {
      value: item.name,
      id: item.id,
      type: "jobTitle",
      createdAt: item.createdAt,
      modifiedAt: item.modifiedAt,
    };
    return param;
  });

  return (
    <div>
      <div className="default-grid ">
        <div className="default-1-col">
          <DropdownComponent
            options={contractTypes}
            label={`${translateText(
              "Contract type",
              "pt"
            )} ${MandatorySignal()}`}
            fieldName="contractType"
            currentObject={employee}
            getValue={employee?.contractType}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "contractType")
                ?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <InputNumberComponent
            label={`${translateText(
              "Employee number",
              "pt"
            )} ${MandatorySignal()}`}
            fieldName="employeeNumber"
            currentObject={employee}
            getValue={employee?.employeeNumber}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "employeeNumber")
                ?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-2-cols">
          <InputTextComponent
            label={`${translateText(
              "Professional email",
              "pt"
            )} ${MandatorySignal()}`}
            fieldName="emailAddress"
            currentObject={employee}
            getValue={employee?.emailAddress}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "emailAddress")
                ?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <InputTextComponent
            label={translateText("Professional phone", "pt")}
            fieldName="professionalPhoneNumber"
            currentObject={employee}
            getValue={employee?.professionalPhoneNumber}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find(
                (item) => item.field === "professionalPhoneNumber"
              )?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <DropdownComponent
            options={peopleManagers}
            label={`${translateText("Manager", "pt")} ${MandatorySignal()}`}
            isValueId={true}
            fieldName="managerAssociated"
            currentObject={employee}
            getValue={
              employee?.managerAssociated?.id
                ? employee?.managerAssociated?.id
                : "none"
            }
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "managerAssociated")
                ?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <DropdownComponent
            options={jobTitles}
            label={`${translateText("Function", "pt")} ${MandatorySignal()}`}
            isValueId={true}
            fieldName="jobTitle"
            currentObject={employee}
            getValue={employee?.jobTitle.id}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "jobTitle")?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <DropdownComponent
            options={workType}
            label={`${translateText(
              "Function type",
              "pt"
            )} ${MandatorySignal()}`}
            fieldName="workType"
            currentObject={employee}
            getValue={employee?.workType}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "workType")?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <DropdownComponent
            options={isSuportService}
            replaceArray={replaceArraySupport}
            label={`${translateText(
              "Remote Worker",
              "pt"
            )} ${MandatorySignal()}`}
            fieldName="isRemoteWorker"
            currentObject={employee}
            getValue={
              employee?.isRemoteWorker === true
                ? "Sim"
                : employee?.isRemoteWorker === false
                ? "Não"
                : ""
            }
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "isRemoteWorker")
                ?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <DropdownComponent
            options={isSuportService}
            replaceArray={replaceArraySupport}
            label={`${translateText(
              "Support Service",
              "pt"
            )} ${MandatorySignal()}`}
            fieldName="isSuportService"
            currentObject={employee}
            getValue={
              employee?.isSuportService === true
                ? "Sim"
                : employee?.isSuportService === false
                ? "Não"
                : ""
            }
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "isSuportService")
                ?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <DropdownComponent
            options={workCountry}
            label={`${translateText(
              "Work country",
              "pt"
            )} ${MandatorySignal()}`}
            fieldName="workCountry"
            currentObject={employee}
            getValue={employee?.workCountry}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "workCountry")
                ?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <DropdownComponent
            options={workLocationsOptions?.data ?? []}
            label={`${translateText("Work city", "pt")} ${MandatorySignal()}`}
            fieldName="workLocation"
            currentObject={employee}
            getValue={employee?.workLocation}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "workLocation")
                ?.invalid
            }
            isDisable={!canEdit || !employee?.workCountry}
          />
        </div>

        <div className="default-1-col">
          <InputNumberComponent
            label={`Horas de trabalho por dia ${MandatorySignal()}`}
            fieldName="workHoursPerDay"
            currentObject={employee}
            getValue={employee?.workHoursPerDay}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find(
                (item) => item.field === "workHoursPerDay"
              )?.invalid
            }
          />
        </div>

        <div className="default-1-col">
          <InputDateComponent
            label={`${translateText("Expiry date rfi", "pt")} ${
              employee?.contractType === "CPS Internacional"
                ? MandatorySignal()
                : ""
            }`}
            fieldName="rfiExpiresAt"
            currentObject={employee}
            getValue={employee?.rfiExpiresAt}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "rfiExpiresAt")
                ?.invalid
            }
            isDisable={
              !canEdit || employee?.contractType !== "CPS Internacional"
            }
          />
        </div>

        <div className="default-1-col">
          <InputNumberComponent
            label={`${translateText("Last year vacation days", "pt")} ${
              employee?.contractType === "CTI" ||
              employee?.contractType === "CTP"
                ? MandatorySignal()
                : ""
            }`}
            fieldName="vacationDaysLastYear"
            currentObject={employee}
            getValue={employee?.vacationDaysLastYear}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find(
                (item) => item.field === "vacationDaysLastYear"
              )?.invalid
            }
            isDisable={
              !canEdit ||
              (employee?.contractType !== "CTI" &&
                employee?.contractType !== "CTP")
            }
          />
        </div>

        <div className="default-1-col">
          <InputNumberComponent
            label={`${translateText("Current year vacation days", "pt")} ${
              employee?.contractType === "CTI" ||
              employee?.contractType === "CTP"
                ? MandatorySignal()
                : ""
            }`}
            fieldName="vacationDaysCurrentYear"
            currentObject={employee}
            getValue={employee?.vacationDaysCurrentYear}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find(
                (item) => item.field === "vacationDaysCurrentYear"
              )?.invalid
            }
            isDisable={
              !canEdit ||
              (employee?.contractType !== "CTI" &&
                employee?.contractType !== "CTP")
            }
          />
        </div>

        <div className="default-1-col">
          <InputTextComponent
            label={`${translateText("Vehicle number plate", "pt")} ${
              hasDriverLicense && 
             ( employee?.contractType === "CTI" ||
              employee?.contractType === "CTP")
                ? MandatorySignal()
                : ""
            }`}
            fieldName="carRegistration"
            currentObject={employee}
            getValue={employee?.carRegistration}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "carRegistration")
                ?.invalid
            }
            isDisable={
              !canEdit ||
              !hasDriverLicense ||
              (employee?.contractType !== "CTI" &&
                employee?.contractType !== "CTP")
            }
          />
        </div>

        <div className="default-1-col">
          <InputTextComponent
            label={`${translateText("Responsible for the vehicle", "pt")} ${
              hasDriverLicense && 
              (employee?.contractType === "CTI" ||
              employee?.contractType === "CTP")
                ? MandatorySignal()
                : ""
            }`}
            fieldName="carOwner"
            currentObject={employee}
            getValue={employee?.carOwner}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "carOwner")?.invalid
            }
            isDisable={
              !canEdit ||
              !hasDriverLicense ||
              (employee?.contractType !== "CTI" &&
                employee?.contractType !== "CTP")
            }
          />
        </div>

        <div className="default-1-col">
          <InputDateComponent
            label={`${translateText(
              "Admission date",
              "pt"
            )} ${MandatorySignal()}`}
            fieldName="admissionDate"
            currentObject={employee}
            getValue={employee?.admissionDate}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "admissionDate")
                ?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <InputDateComponent
            label={translateText("Contract cancellation date", "pt")}
            fieldName="terminationDate"
            currentObject={employee}
            getValue={employee?.terminationDate}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "terminationDate")
                ?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-1-col">
          <DropdownComponent
            options={employeeStatus}
            replaceArray={replaceArray}
            label={translateText("Status", "pt")}
            fieldName="isActive"
            currentObject={employee}
            getValue={employee?.isActive ? "Ativo" : "Inativo"}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "isActive")?.invalid
            }
            isDisable={!canEdit}
          />
        </div>

        <div className="default-4-cols">
          <InputTextAreaComponent
            label={translateText("Observations", "pt")}
            fieldName="observations"
            currentObject={employee}
            getValue={employee?.observations ? employee?.observations : ""}
            setValue={setEmployee}
            isInvalid={
              employeeFields.find((item) => item.field === "observations")
                ?.invalid
            }
            isDisable={!canEdit}
          />
        </div>
      </div>
    </div>
  );
};
export default ProfessionalInfoForm;
